import type {
  EvidenceApiUsage,
  UsageCapabilityName,
  UsageLimit,
  UsageLimitName,
} from "@/features/auth/api/usage";
import {
  CAPABILITY_NAMES,
  CAPABILITY_ORDER,
  DAILY_LIMIT_NAMES,
  formatBytes,
  formatCapability,
  formatQueuePriority,
  hasQueuePriority,
  SHARED_POOL_NOTE,
  USAGE_LIMIT_NAMES,
} from "@/features/usage/constants";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { useUsage } from "@/features/usage/hooks/useUsage";
import { useMemo } from "react";
import {
  SETTINGS_ACTION,
  SETTINGS_MONO,
  SettingsRow,
  SettingsSection,
} from "./SettingsSection";

const LIMIT_ORDER: UsageLimitName[] = [
  "dailyPrompts",
  "dailyMaps",
  "workspaceStorage",
  "evidenceApiPerMinute",
];

const formatCount = (value: number): string => value.toLocaleString();

const formatPair = (name: UsageLimitName, limit: UsageLimit): string =>
  name === "workspaceStorage"
    ? `${formatBytes(limit.usage)} / ${formatBytes(limit.total!)}`
    : `${formatCount(limit.usage)} / ${formatCount(limit.total!)}`;

const formatEvidenceHeadline = (usage: EvidenceApiUsage): string =>
  `${formatCount(usage.last24Hours)} in the last 24 hours`;

const formatEvidenceDetail = (limit: UsageLimit, usage: EvidenceApiUsage): string =>
  `Limit ${formatCount(limit.total!)} queries per minute (${formatCount(limit.usage)} in the last minute) · ${formatCount(usage.last30Days)} in the last 30 days`;

const formatResets = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

/** A single hairline meter: ink over a faint track; red only when exhausted. */
const Meter = ({ limit }: { limit: UsageLimit }) => {
  const ratio = limit.total ? Math.min(limit.usage / limit.total, 1) : 0;
  const exhausted = !!limit.total && limit.usage >= limit.total;
  return (
    <div
      className="mt-1.5 h-px w-full bg-zinc-200"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={limit.total ?? undefined}
      aria-valuenow={limit.usage}
    >
      <div
        className={`h-[2px] -translate-y-px transition-[width] duration-300 ${exhausted ? "bg-red-700" : "bg-zinc-900"}`}
        style={{ width: `${ratio * 100}%` }}
      />
    </div>
  );
};

export const UsageLimitsCard = () => {
  const { data: usageData, isLoading } = useUsage();
  const { flags } = useFeatureFlags();

  const showReset = useMemo(() => {
    if (!usageData) return false;
    return DAILY_LIMIT_NAMES.some((name) => {
      const limit = usageData.limits[name];
      return limit && !limit.isUnlimited;
    });
  }, [usageData]);

  if (isLoading) {
    return (
      <SettingsSection id="settings-usage" label="Plan & usage">
        <p className="text-sm italic text-zinc-400">Loading usage…</p>
      </SettingsSection>
    );
  }

  if (!usageData) {
    return (
      <SettingsSection id="settings-usage" label="Plan & usage">
        <p className="text-sm italic text-zinc-400">No usage data available.</p>
      </SettingsSection>
    );
  }

  const sharedPool = CAPABILITY_ORDER.some(
    (name) => usageData.capabilities?.[name]?.isSharedPool,
  );

  return (
    <>
      <SettingsSection id="settings-usage" label="Plan & usage">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
          <p className="font-ui text-base leading-7 text-zinc-900">{usageData.planName}</p>
          {showReset && (
            <p className="chat-ed-meta !mt-0">
              Daily counters reset {formatResets(usageData.usageResetsAt)}
            </p>
          )}
        </div>

        <dl className="mt-5 space-y-5">
          {LIMIT_ORDER.map((name) => {
            const limit = usageData.limits[name];
            if (!limit) return null;
            if (name === "evidenceApiPerMinute" && !flags.evidenceApi) return null;
            return (
              <div key={name}>
                <div className="font-ui flex items-baseline justify-between gap-6 text-sm leading-6">
                  <dt className="text-zinc-700">{USAGE_LIMIT_NAMES[name]}</dt>
                  <dd className={`${SETTINGS_MONO} text-zinc-900`}>
                    {limit.isUnlimited ? (
                      <span className="text-zinc-500">unlimited</span>
                    ) : name === "evidenceApiPerMinute" && usageData.evidenceApi ? (
                      formatEvidenceHeadline(usageData.evidenceApi)
                    ) : (
                      formatPair(name, limit)
                    )}
                  </dd>
                </div>
                {!limit.isUnlimited && <Meter limit={limit} />}
                {name === "evidenceApiPerMinute" && usageData.evidenceApi && (
                  <p className="mt-1.5 text-xs leading-5 text-zinc-400">
                    {formatEvidenceDetail(limit, usageData.evidenceApi)}
                  </p>
                )}
              </div>
            );
          })}
        </dl>

        <p className="mt-6 text-sm leading-6 text-zinc-500">
          Need more?{" "}
          <a
            href="mailto:support@gxl.ai?subject=Request for Increased Usage Limit"
            className={`${SETTINGS_ACTION} text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-900`}
          >
            Request higher limits ↗
          </a>
        </p>
      </SettingsSection>

      {usageData.capabilities && (
        <SettingsSection id="settings-capabilities" label="Capabilities">
          <dl>
            {CAPABILITY_ORDER.map((name: UsageCapabilityName) => {
              const capability = usageData.capabilities?.[name];
              if (!capability) return null;
              return (
                <SettingsRow key={name} label={CAPABILITY_NAMES[name]}>
                  <span className={SETTINGS_MONO}>
                    {capability.isUnlimited
                      ? "unlimited"
                      : formatCapability(name, capability.total!, capability.isSharedPool ?? false)}
                  </span>
                </SettingsRow>
              );
            })}
            {hasQueuePriority(usageData.capabilities) && (
              <SettingsRow label="Queue priority">
                {formatQueuePriority(usageData.capabilities.heavyQueuePriority)}
              </SettingsRow>
            )}
          </dl>
          {sharedPool && (
            <p className="mt-3 text-xs leading-5 text-zinc-400">{SHARED_POOL_NOTE}.</p>
          )}
        </SettingsSection>
      )}
    </>
  );
};
